import { fetchQuery, graphql } from "relay-runtime"
import { create } from "zustand"
import { getUser, mandatorySofiaPreCheckFailed } from "@/apis/bff"
import { fetchUsersQuery, fetchUsersQuery$data } from "@/lib/__generated__/fetchUsersQuery.graphql"
import { cacheOrDefaultWithLifetime } from "@/lib/cache-helper"
import { RelayEnvironment } from "@/relay-environment"

export type UserData = fetchUsersQuery$data["users"][0]
export type RoleDataUser = fetchUsersQuery$data["loginUser"][0]

interface UserStore {
  users: Map<string, UserData> | null
  loginUser: RoleDataUser | null
  isAtossDataMismatchedOrMissing: boolean
  setUsers: (userDatas: Map<string, UserData>) => void
  setLoginUser: (loginUser: RoleDataUser) => void
  setIsAtossDataMismatchedOrMissing: (isAtossDataMismatchedOrMissing: boolean) => void
}

export const useUserStore = create<UserStore>((set) => ({
  users: null,
  loginUser: null,
  isAtossDataMismatchedOrMissing: false,
  setUsers: (users) => {
    set({ users })
  },
  setLoginUser: (loginUser) => {
    set({ loginUser })
  },
  setIsAtossDataMismatchedOrMissing: (isUserInformationNotMatch) => {
    set({ isAtossDataMismatchedOrMissing: isUserInformationNotMatch })
  },
}))

// We only cache users 5 minutes as there could be implications for the chat, especially when going to a recently deployed test environment
const HourInMs = 5 * 60 * 1000

export const fetchUsers = async () => {
  const data = await cacheOrDefaultWithLifetime("users", HourInMs, async () => {
    const { user } = await getUser()
    return fetchQuery<fetchUsersQuery>(
      RelayEnvironment,
      graphql`
        query fetchUsersQuery($input: String!) {
          users {
            userName
            firstName
            lastName
            role
            isDeleted
            professionalTitle
            globalLocationNumber
            acsUserId
            roleData {
              ... on Doctor {
                specialties {
                  skill
                }
                licensesInCountries {
                  country
                  isFavoredInCountry
                }
              }
            }
          }
          loginUser: users(where: { userName: { eq: $input } }) {
            userName
            role
            hasTelematikId
            roleData {
              ... on Doctor {
                languages {
                  fullySpottable
                  skill
                }
                preferredLanguage
              }
              ... on CallCenterAgent {
                ccaLanguages: languages
                preferredLanguage
              }
            }
          }
        }
      `,
      { input: user.userName },
    ).toPromise()
  })

  if (!data) {
    throw Error("Failed to fetch users")
  }

  setInitialState(data)
}

const preCheckUser = async (users: Map<string, UserData>) => {
  const { user } = await getUser()
  const sofiaUser = users.get(user.userName)
  if (!sofiaUser) {
    mandatorySofiaPreCheckFailed(
      "The user seems to be misconfigured in EntraId/AzureAD. Please check FirstName, LastName, OnPremisesSamAccountName, and Mail. They are mandatory and must not be empty.",
    )
  }

  if (user.isOneOf(["Doctor", "MedicalSupervisor", "NursingProfessional"])) {
    const isAtossDataMismatchedOrMissing =
      user.countries.length === sofiaUser.roleData?.licensesInCountries?.length &&
      user.countries.every((country) => sofiaUser.roleData?.licensesInCountries?.some((lic) => lic.country === country))

    useUserStore.getState().setIsAtossDataMismatchedOrMissing(!isAtossDataMismatchedOrMissing)
  }

  if (user.isOneOf(["Doctor", "MedicalSupervisor"]) && !sofiaUser.globalLocationNumber) {
    mandatorySofiaPreCheckFailed(
      "Having a Global Location Number (GLN) is mandatory for roles Doctor or Medical supervisor. Please contact IT-Helpdesk to have it setup in Active Directory.",
    )
  }
}

function setInitialState(data: fetchUsersQuery$data) {
  const users = new Map(data.users.map((user) => [user.userName, user]))

  useUserStore.getState().setUsers(users)
  useUserStore.getState().setLoginUser(data.loginUser[0])
  preCheckUser(users)
}
